import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { companiesApi } from "@/api/companies";
import { providerAccountsApi, type UsageAgent } from "@/api/provider-accounts";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Building2, Loader2 } from "lucide-react";

/**
 * Tenant token-usage dashboard — per-agent input/output tokens, turns,
 * errors and the background (no-human) share, regardless of whether the
 * agent runs on OAuth, Foundry or a direct API key. Fed by the per-turn
 * runtime telemetry ingested into agent_telemetry.
 */
export function TenantAdminUsage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [days, setDays] = useState(7);

  const { data: company } = useQuery({
    queryKey: queryKeys.companies.detail(tenantId!),
    queryFn: () => companiesApi.get(tenantId!),
    enabled: !!tenantId,
  });

  const { data: usage, isLoading } = useQuery({
    queryKey: ["tenant-usage", tenantId, days],
    queryFn: () => providerAccountsApi.usage(tenantId!, days),
    enabled: !!tenantId,
    refetchInterval: 60_000,
  });

  if (!tenantId) return null;

  const fmt = (n: number) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n);

  const totalIn = usage?.agents.reduce((s, a) => s + a.inputTokens, 0) ?? 0;
  const totalOut = usage?.agents.reduce((s, a) => s + a.outputTokens, 0) ?? 0;
  const totalTurns = usage?.agents.reduce((s, a) => s + a.turns, 0) ?? 0;
  const totalBackground = usage?.agents.reduce((s, a) => s + a.background, 0) ?? 0;
  const maxDailyIn = Math.max(1, ...(usage?.daily.map((d) => d.inputTokens) ?? [1]));

  function providerSummary(a: UsageAgent): string {
    return a.providers
      .slice()
      .sort((x, y) => y.inputTokens - x.inputTokens)
      .map((p) => `${p.provider} (${p.turns})`)
      .join(", ");
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="shrink-0 border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => navigate("/tenants")}>
            <ArrowLeft size={14} className="mr-1.5" />
            Tenants
          </Button>
          <span className="text-text-muted">/</span>
          <span className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
            <Building2 size={14} />
            {company?.name ?? "…"}
          </span>
          <span className="text-text-muted">/</span>
          <span className="text-sm text-text-secondary">Token usage</span>
          <div className="ml-auto flex items-center gap-1">
            {[1, 7, 30].map((d) => (
              <Button key={d} size="sm" variant={days === d ? "default" : "ghost"} onClick={() => setDays(d)}>
                {d === 1 ? "24h" : `${d}d`}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-6">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={22} className="animate-spin text-text-muted" />
          </div>
        ) : (
          <>
            {/* Totals */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                { label: "Input tokens", value: fmt(totalIn) },
                { label: "Output tokens", value: fmt(totalOut) },
                { label: "Turns", value: fmt(totalTurns) },
                {
                  label: "Background share",
                  value: totalTurns ? `${Math.round((totalBackground / totalTurns) * 100)}%` : "—",
                },
              ].map((s) => (
                <Card key={s.label}>
                  <CardContent className="py-3">
                    <p className="text-xs text-text-muted">{s.label}</p>
                    <p className="text-xl font-semibold text-text-primary">{s.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Daily input-token bars */}
            <Card>
              <CardContent className="py-4">
                <p className="mb-3 text-sm font-medium text-text-primary">Daily input tokens</p>
                {usage && usage.daily.length > 0 ? (
                  <div className="flex h-28 items-end gap-1">
                    {usage.daily.map((d) => (
                      <div key={d.day} className="group flex flex-1 flex-col items-center gap-1">
                        <div
                          className="w-full rounded-t bg-primary/70 transition-colors group-hover:bg-primary"
                          style={{ height: `${Math.max(3, (d.inputTokens / maxDailyIn) * 100)}%` }}
                          title={`${d.day}: in ${fmt(d.inputTokens)} / out ${fmt(d.outputTokens)} / ${d.turns} turns`}
                        />
                        <span className="text-[10px] text-text-muted">{d.day.slice(5)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-text-muted">No telemetry yet — data appears as agents take turns.</p>
                )}
              </CardContent>
            </Card>

            {/* Per-agent table */}
            <Card>
              <CardContent className="py-4">
                <p className="mb-3 text-sm font-medium text-text-primary">By agent (last {days === 1 ? "24h" : `${days} days`})</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-text-muted">
                        <th className="py-2 pr-3 font-medium">Agent</th>
                        <th className="py-2 pr-3 text-right font-medium">Input</th>
                        <th className="py-2 pr-3 text-right font-medium">Output</th>
                        <th className="py-2 pr-3 text-right font-medium">Turns</th>
                        <th className="py-2 pr-3 text-right font-medium">Background</th>
                        <th className="py-2 pr-3 text-right font-medium">Errors</th>
                        <th className="py-2 font-medium">Providers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage?.agents.map((a) => (
                        <tr key={`${a.agentId}-${a.agentSlug}`} className="border-b border-border/50">
                          <td className="py-2 pr-3 text-text-primary">{a.name}</td>
                          <td className="py-2 pr-3 text-right font-mono text-xs">{fmt(a.inputTokens)}</td>
                          <td className="py-2 pr-3 text-right font-mono text-xs">{fmt(a.outputTokens)}</td>
                          <td className="py-2 pr-3 text-right font-mono text-xs">{a.turns}</td>
                          <td className="py-2 pr-3 text-right font-mono text-xs">
                            {a.turns ? `${Math.round((a.background / a.turns) * 100)}%` : "—"}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-xs">
                            {a.errors > 0 ? <span className="text-red-500">{a.errors}</span> : "0"}
                          </td>
                          <td className="py-2 text-xs text-text-muted">{providerSummary(a)}</td>
                        </tr>
                      ))}
                      {usage?.agents.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-4 text-center text-xs text-text-muted">
                            No usage recorded in this window.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-[11px] text-text-muted">
                  Input includes provider cache reads. "Background" = turns with no human user attached
                  (crons, workflows, heartbeats) — a high share here is where quota quietly leaks.
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
